import { datadogRum } from '@datadog/browser-rum';
import { useSession } from '@side/next-auth/react';
import { isBefore } from 'date-fns';
import { useEffect } from 'react';
import type { IdentityRequest } from '$api-clients/hooks';
import { useIdentity } from '$api-clients/hooks';
import { Scopes } from '$authentication/useHasPermission/constants';
import { useHasPermission } from '$authentication/useHasPermission/useHasPermission';
import { RESTRICTED_STATES_BY_FEATURE } from './constants';
import type { AppFeature } from './types';

// Matches the imperative query this hook previously issued so the SWR cache key
// (and the upstream request) is byte-for-byte identical to the legacy behaviour.
const AGENT_TEAM_MEMBER_SEARCH =
  'status^("ACTIVE","ONBOARDING","OFFBOARDING") AND roles:"AGENT"';

type SubmitAsTeamMember = NonNullable<
  IdentityRequest<'/users/{userId}/interaction-permissions/{type}/team-members'>['Data']['data']
>[number];
type UserInfoTeam = NonNullable<
  IdentityRequest<'/auth/userinfo'>['Data']['teams']
>[number];
type IdentityLicense = SubmitAsTeamMember['license'];

const isLicenseExpired = (license: NonNullable<IdentityLicense>) => {
  if (!license.expiration) return true;

  return isBefore(new Date(license.expiration), new Date());
};

/**
 * Declarative replacement for the previous effect-driven implementation.
 *
 * The old version fired `identityApi.GET('/auth/userinfo')` + a `submit-as`
 * team-members call imperatively inside a `useEffect` keyed on the `user`
 * object. Those imperative calls bypassed SWR's cache/deduping, so every
 * session refetch (which produces a new `user` reference) and every mounted
 * consumer issued fresh network requests — the dominant contributor to TXM's
 * request bursts.
 *
 * Routing both reads through `useIdentity` means all consumers share one
 * cache entry per key and re-renders/remounts are served from cache. The
 * decision is derived synchronously from the cached data; nothing fetches in
 * an effect. Mirrors audit-ui's `useHasAccessToFeatureSwr`.
 *
 * Only georestricted features (currently just `LEASES`) are gated here; any
 * other feature has no access, matching the previous `default: false` switch
 * branch. Returns `undefined` while a required upstream read is still loading
 * so callers can render a stable "checking" state.
 */
export function useHasAccessToFeature(
  feature: AppFeature,
): boolean | undefined {
  const {
    hasPermission: hasInteractionsReadPermission,
    loading: interactionsReadLoading,
  } = useHasPermission({
    roles: ['ADMIN'],
    scopes: [Scopes.INTERACTIONS_READ],
  });

  // Read synchronously for the decision — NOT as an effect dependency, so an
  // unstable `user` reference can no longer trigger refetches.
  const user = useSession().data?.user;
  const userId = user?.id;
  const roles = user?.roles ?? [];

  const restrictedStates =
    RESTRICTED_STATES_BY_FEATURE[
      feature as keyof typeof RESTRICTED_STATES_BY_FEATURE
    ];

  const directAccess =
    roles.includes('ADMIN') ||
    roles.includes('AUDITOR') ||
    hasInteractionsReadPermission;

  // Only the georestricted path needs the license lookups. Admins/auditors and
  // non-georestricted features short-circuit before any network call — so most
  // renders fetch nothing here.
  const needsLicenseCheck =
    Boolean(userId) && Boolean(restrictedStates) && !directAccess;

  const {
    data: userInfo,
    isLoading: userInfoLoading,
    error: userInfoError,
  } = useIdentity('/auth/userinfo', needsLicenseCheck ? undefined : null);

  const {
    data: submitAsResponse,
    isLoading: submitAsLoading,
    error: submitAsError,
  } = useIdentity(
    '/users/{userId}/interaction-permissions/{type}/team-members',
    needsLicenseCheck && userId
      ? {
          params: {
            path: { userId, type: 'submit-as' },
            query: { limit: 500, search: AGENT_TEAM_MEMBER_SEARCH },
          },
        }
      : null,
  );

  // Without this, an API outage during the georestriction check is
  // indistinguishable from "user has no access" — the hook returns `false`
  // either way (data is undefined). Reporting the error preserves the
  // observability the old imperative implementation had.
  useEffect(() => {
    const error = userInfoError ?? submitAsError;
    if (error !== undefined) {
      datadogRum.addError(error, {
        userId,
        message: 'Error determining geolocation restricted feature access',
      });
    }
  }, [userInfoError, submitAsError, userId]);

  if (interactionsReadLoading) return undefined;
  if (!userId) return undefined;

  // Non-georestricted / unknown feature — parity with the old `default: false`
  // switch branch.
  if (!restrictedStates) return false;
  if (directAccess) return true;

  if (userInfoLoading || submitAsLoading) return undefined;

  const activeTeamIds = new Set(
    Object.keys(userInfo?.accessibleTeams?.nodes ?? {}),
  );
  const currentUserTeams: UserInfoTeam[] = userInfo?.teams ?? [];
  const currentUserActiveTeamMarkets = currentUserTeams
    .filter((team) => activeTeamIds.has(team.id))
    .map((team) => team.market);

  const submitAsTeamMembers: SubmitAsTeamMember[] =
    submitAsResponse?.data ?? [];

  return (
    submitAsTeamMembers.some(
      ({ license }) =>
        license &&
        !isLicenseExpired(license) &&
        !restrictedStates.has(license.state),
    ) ||
    currentUserActiveTeamMarkets.some(
      (market) => market && !restrictedStates.has(market),
    )
  );
}
